import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight, Building2, Hospital, Coins, Cpu, GraduationCap, ShoppingBag,
  Clock, ShieldAlert, Bug, Zap, CheckCircle2, AlertTriangle, Sparkles,
} from "lucide-react";

/**
 * /customers — Customer Stories page.
 *
 * IMPORTANT (GSGD honest-copy rule):
 *   Every story on this page is an ILLUSTRATIVE SCENARIO. Names, headcounts,
 *   and exact numbers are representative — the workflows themselves match
 *   real things ArgusSecure does (SCA → AI Fix-It → close audit finding, etc.).
 *   Replace any of these with a real, attributed customer story once you have
 *   permission to publish.
 */

const SCENARIOS = [
  {
    id: "northbridge",
    company: "Northbridge",
    industry: "Fintech · Series B",
    size: "42 engineers · 11 services",
    Icon: Coins,
    headline: "Cleared a SOC 2 dependency-audit finding in 9 days",
    challenge:
      "External SOC 2 auditor flagged outdated Node + Python deps across the auth and ledger services. The team had 14 days to remediate or document exceptions.",
    actions: [
      "Imported 11 service manifests via ArgusSecure SCA",
      "AI Fix-It Agent generated upgrade PRs for 17 of the 22 CVEs (Claude Sonnet 4.5)",
      "5 deferred CVEs documented as exceptions with rationale in the audit log",
      "Findings exported as a signed PDF report for the auditor",
    ],
    outcomes: [
      { label: "CVEs remediated", value: "17 / 22" },
      { label: "Time from import to clean report", value: "9 days" },
      { label: "Auditor findings closed", value: "1 / 1" },
    ],
    scanners: ["SCA", "AI Fix-It", "Reports"],
  },
  {
    id: "halcyon",
    company: "Halcyon Health",
    industry: "Health-tech · Series A",
    size: "18 engineers · HIPAA-regulated",
    Icon: Hospital,
    headline: "Stopped a critical S3 misconfig before launch",
    challenge:
      "Patient-facing portal was 6 days from going live. A junior engineer had inadvertently published an S3 bucket with PHI export CSVs publicly readable via ACL.",
    actions: [
      "Connected an AWS IAM read-only role on the ArgusSecure Integrations page",
      "Triggered a Cloud · AWS S3 scan from the Integrations tab",
      "Critical finding ‘public ACL on PHI exports’ surfaced in 14 seconds",
      "AI Fix-It generated the exact `aws s3api put-public-access-block` commands",
    ],
    outcomes: [
      { label: "PHI records exposed", value: "0" },
      { label: "Detection time", value: "14 sec" },
      { label: "Time to remediate", value: "21 min" },
    ],
    scanners: ["Cloud · AWS S3", "AI Fix-It"],
  },
  {
    id: "stencil",
    company: "Stencil",
    industry: "Dev-tools SaaS · Seed",
    size: "9 engineers · 1 monorepo",
    Icon: Cpu,
    headline: "Caught an unauth IDOR before a customer did",
    challenge:
      "A staging deploy of a new tenant-scoped report API was missing the org-id check. Found during a routine pre-release DAST scan.",
    actions: [
      "DAST project pointed at staging.stencil.dev (domain verified via DNS TXT)",
      "Built-in DAST flagged the unauth IDOR endpoint with the raw request + response as evidence",
      "Auth team applied the fix the same afternoon, re-scanned, finding closed",
    ],
    outcomes: [
      { label: "Severity caught", value: "Critical" },
      { label: "Customers affected", value: "0" },
      { label: "Re-scan to clean", value: "Same day" },
    ],
    scanners: ["DAST", "Domain verification"],
  },
  {
    id: "altcohort",
    company: "Alt Cohort",
    industry: "Edtech · Bootstrapped",
    size: "4 engineers · 1 founder is the AppSec lead",
    Icon: GraduationCap,
    headline: "First scan found the bug they'd been chasing for a week",
    challenge:
      "Founders had been debugging an intermittent SQL error on the grading service. Suspected library bug. Was actually classic SQL injection via unparameterised query.",
    actions: [
      "Uploaded the grading service source as a SAST project",
      "Scanner flagged `cursor.execute(f\"SELECT … WHERE id = {uid}\")` (CWE-89, OWASP A05:2025)",
      "AI Fix-It produced the parameterised-query patch",
      "Founder merged the PR within 30 minutes",
    ],
    outcomes: [
      { label: "Bugs found in first scan", value: "3" },
      { label: "Onboarding to first finding", value: "8 min" },
      { label: "Setup cost", value: "Free tier" },
    ],
    scanners: ["SAST", "AI Fix-It"],
  },
  {
    id: "mercatorpay",
    company: "MercatorPay",
    industry: "Payments · Series C",
    size: "180 engineers · 40+ services",
    Icon: Building2,
    headline: "Replaced two scanners with one, saved $84k/yr",
    challenge:
      "Were paying for separate vendors for SAST and SCA, plus an internal Burp/ZAP setup for DAST. Findings lived in three different inboxes, with three different triage workflows.",
    actions: [
      "Migrated 40+ projects to ArgusSecure across 6 weeks (CI tokens + REST API)",
      "Pointed ArgusSecure at their existing OWASP ZAP daemon via the BYO integration",
      "Findings unified into a single inbox with consistent severity + OWASP 2025 tagging",
      "Sunset both legacy vendors at end of contract",
    ],
    outcomes: [
      { label: "Annual licence saved", value: "≈$84k" },
      { label: "Triage inboxes", value: "3 → 1" },
      { label: "OWASP tag consistency", value: "100%" },
    ],
    scanners: ["SAST", "SCA", "DAST · ZAP", "API tokens"],
  },
  {
    id: "tideline",
    company: "Tideline Commerce",
    industry: "E-commerce · Bootstrapped",
    size: "12 engineers · React + Node",
    Icon: ShoppingBag,
    headline: "Used the AI Fix-It Agent to onboard a junior engineer",
    challenge:
      "New junior engineer needed to learn the team's security expectations. Reading the OWASP Top 10 docs cold wasn't sticking.",
    actions: [
      "Engineer was given access to ArgusSecure findings as a triage exercise",
      "For each finding, asked the AI Fix-It Agent: ‘explain this CWE in plain English’",
      "Worked through 20 findings across SAST + SCA in one week",
      "By week 2, engineer was authoring PRs on real bugs without supervision",
    ],
    outcomes: [
      { label: "Onboarding programme length", value: "2 weeks → 1 week" },
      { label: "Findings triaged solo", value: "20" },
      { label: "Senior review hours saved", value: "≈8 hrs/wk" },
    ],
    scanners: ["AI Fix-It", "Findings inbox"],
  },
];

const PERSONAS = [
  {
    id: "mia",
    name: "Mia",
    title: "AppSec lead",
    company_kind: "Fintech, Series B",
    Icon: Coins,
    day: [
      { time: "08:50", action: "Open the ArgusSecure Dashboard. Severity donut shows 3 new criticals overnight (CI scans during the freeze window).", focus: "Dashboard" },
      { time: "09:05", action: "Click the OWASP 2025 donut → 2 of the 3 are A03 Supply Chain. SCA scanner caught a fresh CVE in `node-tar`.", focus: "OWASP filter" },
      { time: "09:12", action: "Tap AI Fix-It on the first finding. Patch generated. Open PR in 4 minutes.", focus: "AI Fix-It" },
      { time: "10:30", action: "Stand-up — show the team the unified inbox. Triage the remaining critical (false positive — mark + rationale).", focus: "Findings" },
      { time: "14:00", action: "Export a signed PDF for the security committee meeting on Friday.", focus: "Reports" },
      { time: "16:45", action: "Goddy bubble pops up: ‘Verification on staging.fintechco.com is about to expire — reverify?’", focus: "Goddy agent" },
    ],
    quote: "Three scanners became one inbox. The AI Fix-It is what made this stick — engineers don't dread triage anymore.",
    note: "Illustrative day · names changed",
  },
  {
    id: "dev",
    name: "Dev",
    title: "Solo founder + CTO",
    company_kind: "Edtech, bootstrapped",
    Icon: GraduationCap,
    day: [
      { time: "07:20", action: "Free-tier sign-up. Verifies email, lands on the Welcome tour.", focus: "Signup" },
      { time: "07:25", action: "Drags `package.json` into the new-project wizard. SCA scan completes in 6 seconds. 2 medium CVEs.", focus: "SCA" },
      { time: "07:35", action: "Pastes the main Python service file. SAST scan flags one hardcoded password. Eyes widen.", focus: "SAST" },
      { time: "07:42", action: "Asks Goddy: ‘what's the simplest way to fix this in Flask?’ — gets a Click-to-copy snippet.", focus: "Goddy agent" },
      { time: "08:10", action: "Commits the fix. Re-runs scans from the project page. 0 open criticals.", focus: "Scan loop" },
      { time: "11:00", action: "Decides ArgusSecure is going into the CI pipeline. Generates an API token. Reads the docs.", focus: "API tokens" },
    ],
    quote: "I'm not a security person. I just want to ship without leaking customer data. This is the first tool that helped me do that.",
    note: "Illustrative day · names changed",
  },
];

function Disclaimer() {
  return (
    <div className="border border-gold/30 bg-gold/5 rounded-lg p-4 flex items-start gap-3" data-testid="customers-disclaimer">
      <AlertTriangle size={16} className="text-gold mt-0.5 shrink-0" />
      <div className="text-xs text-ink/80 leading-relaxed">
        <strong className="text-ink">A note on these stories.</strong>{" "}
        Every scenario below is <strong className="text-gold">illustrative</strong>.
        Company names, headcounts, and timeline numbers are representative — the
        workflows themselves match real things ArgusSecure does (SCA, DAST, the
        OWASP ZAP engine, the AI Fix-It Agent, cloud-asset scanning). We don't
        publish fabricated testimonials or invented quotes. When a real customer
        is willing to be named and quoted, their story replaces an illustrative
        one here — with a clear "Real customer" badge.
      </div>
    </div>
  );
}

function ScenarioCard({ s }) {
  const Icon = s.Icon;
  return (
    <article
      className="rounded-2xl border border-line bg-panel p-7 lg:p-9 grid lg:grid-cols-[260px_1fr] gap-7"
      data-testid={`scenario-${s.id}`}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg border border-line bg-raised flex items-center justify-center">
            <Icon size={20} className="text-gold" />
          </div>
          <div>
            <div className="font-display text-xl leading-tight">{s.company}</div>
            <div className="text-[11px] uppercase tracking-wider text-ink/45 mt-0.5">Illustrative</div>
          </div>
        </div>
        <div className="text-xs text-ink/60 space-y-1">
          <div>{s.industry}</div>
          <div>{s.size}</div>
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {s.scanners.map((tag) => (
            <span key={tag} className="text-[10px] uppercase tracking-wider border border-line bg-paper/50 rounded-full px-2 py-0.5 text-ink/70">{tag}</span>
          ))}
        </div>
      </div>

      <div className="space-y-5 min-w-0">
        <h3 className="font-display text-2xl lg:text-3xl leading-tight text-ink">{s.headline}</h3>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink/45 mb-1.5">The challenge</div>
          <p className="text-sm text-ink/80 leading-relaxed">{s.challenge}</p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink/45 mb-1.5">What they did</div>
          <ul className="space-y-1.5">
            {s.actions.map((a, i) => (
              <li key={`a-${i}-${a.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-ink/85">
                <CheckCircle2 size={14} className="text-gold mt-0.5 shrink-0" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border-t border-line/60 pt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink/45 mb-2.5">Outcome</div>
          <div className="grid grid-cols-3 gap-3">
            {s.outcomes.map((o) => (
              <div key={o.label} className="rounded-md border border-line bg-paper/40 p-2.5">
                <div className="font-display text-lg lg:text-xl text-gold leading-none">{o.value}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/55 mt-1.5">{o.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

function PersonaCard({ p }) {
  const Icon = p.Icon;
  return (
    <article className="rounded-2xl border border-line bg-panel p-7 lg:p-8 space-y-5" data-testid={`persona-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg border border-line bg-raised flex items-center justify-center">
            <Icon size={20} className="text-gold" />
          </div>
          <div>
            <div className="font-display text-xl leading-tight">{p.name}</div>
            <div className="text-xs text-ink/55">{p.title} · {p.company_kind}</div>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider border border-line bg-paper/50 rounded-full px-2 py-0.5 text-ink/55 shrink-0">{p.note}</span>
      </div>

      <ol className="space-y-2.5 border-l border-line/70 pl-4">
        {p.day.map((step, i) => (
          <li key={`${p.id}-${step.time}-${i}`} className="grid grid-cols-[56px_1fr] gap-3 items-start">
            <div className="font-mono text-xs text-gold pt-0.5">{step.time}</div>
            <div>
              <div className="text-sm text-ink/85 leading-snug">{step.action}</div>
              <div className="text-[10px] uppercase tracking-wider text-ink/40 mt-1">{step.focus}</div>
            </div>
          </li>
        ))}
      </ol>

      <blockquote className="border-l-2 border-gold pl-4 italic text-ink/80 text-sm leading-relaxed">
        "{p.quote}"
      </blockquote>
    </article>
  );
}

export default function Customers() {
  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col" data-testid="customers-page">
      <MarketingHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-10 lg:pt-28 w-full">
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Customer Stories</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
          Realistic scenarios. <em className="text-gold">Honest labels.</em>
        </h1>
        <p className="text-ink/70 mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
          We refuse to publish fake testimonials. Below are six illustrative
          scenarios + two day-in-the-life walkthroughs. Each describes a workflow
          ArgusSecure genuinely supports today — only the names are synthetic.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button asChild className="bg-gold text-paper hover:bg-gold2 h-11 font-semibold">
            <Link to="/signup" data-testid="customers-cta-signup">Start your own story <ArrowRight size={16} className="ml-2" /></Link>
          </Button>
          <Button asChild variant="outline" className="h-11 border-line bg-transparent text-ink">
            <Link to="/why-argus" data-testid="customers-cta-why">Why ArgusSecure →</Link>
          </Button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-4 w-full">
        <Disclaimer />
      </section>

      {/* Scenarios */}
      <section className="max-w-6xl mx-auto px-6 py-12 w-full space-y-8">
        <h2 className="font-display text-3xl lg:text-4xl">Scenarios</h2>
        <p className="text-sm text-ink/55 -mt-3 max-w-2xl">
          Six representative workflows — by team size, regulated industry, and stage. Each story
          maps to scanners and features that are live in ArgusSecure today.
        </p>
        <div className="space-y-6">
          {SCENARIOS.map((s) => <ScenarioCard key={s.id} s={s} />)}
        </div>
      </section>

      {/* Personas / day in the life */}
      <section className="max-w-6xl mx-auto px-6 py-12 w-full space-y-8">
        <h2 className="font-display text-3xl lg:text-4xl">A day in the life</h2>
        <p className="text-sm text-ink/55 -mt-3 max-w-2xl">
          Two persona walkthroughs. Both names are pseudonyms — the actions match
          real ArgusSecure flows you can reproduce right now in a free trial.
        </p>
        <div className="grid lg:grid-cols-2 gap-6">
          {PERSONAS.map((p) => <PersonaCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* Design-partner CTA */}
      <section className="border-t border-line bg-panel/40">
        <div className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Replace this page</span>
            <h3 className="font-display text-2xl lg:text-3xl mt-2">Your story could go here next.</h3>
            <p className="text-ink/70 mt-3 max-w-2xl text-sm leading-relaxed">
              We're inviting a small cohort of design partners to swap illustrative
              scenarios above for real, attributed stories. NDA-friendly. No
              vapor logos, no fabricated quotes — we'll only publish what you'd
              say out loud at a security conference.
            </p>
          </div>
          <div className="flex gap-3">
            <Button asChild className="bg-gold text-paper hover:bg-gold2 h-11 font-semibold">
              <Link to="/design-partner" data-testid="customers-cta-design-partner">
                Become a design partner <Sparkles size={14} className="ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
